import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { PageCard } from '../../components/layout/page/PageCard';
import { PageHeader } from '../../components/layout/page/PageHeader';
import { PageShell } from '../../components/layout/page/PageShell';
import {
  AccessPermission,
  getRolePermissions,
  listModules,
  listPermissionsCatalog,
  listRoles,
  replaceRolePermissions,
  RoleManagement,
  SystemModule,
} from '../../services/accessControlApi';
import { usePermissions } from '../../contexts/PermissionsContext';
import { resolveAdminModuleLabel } from '../../utils/resolveAdminModuleLabel';

type PermissionModuleGroup = {
  moduleCode: string;
  moduleName: string;
  pillar: string;
  sortOrder: number;
  permissions: AccessPermission[];
};

const AUTOSAVE_TOAST_ID = 'role-permissions-autosave';

export function RolePermissionsPage() {
  const { roleId = '' } = useParams();
  const { canWriteModule } = usePermissions();
  const canWrite = canWriteModule('access-control.roles');
  const [role, setRole] = useState<RoleManagement | null>(null);
  const [catalog, setCatalog] = useState<AccessPermission[]>([]);
  const [modules, setModules] = useState<SystemModule[]>([]);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const saveSequenceRef = useRef(0);

  useEffect(() => {
    void (async () => {
      setLoading(true);
      try {
        const [roles, permissions, assigned, moduleResult] = await Promise.all([
          listRoles({ page: 1, pageSize: 200 }),
          listPermissionsCatalog(),
          getRolePermissions(roleId),
          listModules({ page: 1, pageSize: 200 }),
        ]);
        setRole(roles.items.find((item) => item.id === roleId) ?? null);
        setCatalog(permissions);
        setModules(moduleResult.items);
        setSelected(new Set(assigned.map((item) => item.id)));
      } catch {
        toast.error('Falha ao carregar permissões.');
      } finally {
        setLoading(false);
      }
    })();
  }, [roleId]);

  const grouped = useMemo(() => {
    const moduleByCode = new Map(modules.map((module) => [module.code, module]));
    const map = new Map<string, AccessPermission[]>();
    for (const permission of catalog) {
      const list = map.get(permission.moduleCode) ?? [];
      list.push(permission);
      map.set(permission.moduleCode, list);
    }

    return Array.from(map.entries())
      .map(([moduleCode, permissions]): PermissionModuleGroup => {
        const module = moduleByCode.get(moduleCode);
        return {
          moduleCode,
          moduleName: resolveAdminModuleLabel(module?.name ?? moduleCode, module?.pillar),
          pillar: module?.pillar ?? '',
          sortOrder: module?.sortOrder ?? Number.MAX_SAFE_INTEGER,
          permissions,
        };
      })
      .sort((left, right) => left.sortOrder - right.sortOrder || left.moduleName.localeCompare(right.moduleName));
  }, [catalog, modules]);

  async function persistPermissions(nextSelected: Set<string>): Promise<void> {
    if (!canWrite) return;

    const sequence = ++saveSequenceRef.current;
    setSaving(true);
    try {
      await replaceRolePermissions(roleId, Array.from(nextSelected));
      if (sequence !== saveSequenceRef.current) return;
      toast.success('Permissões salvas.', { toastId: AUTOSAVE_TOAST_ID });
    } catch {
      if (sequence !== saveSequenceRef.current) return;
      toast.error('Falha ao salvar permissões.');
    } finally {
      if (sequence === saveSequenceRef.current) {
        setSaving(false);
      }
    }
  }

  function togglePermission(permissionId: string, checked: boolean): void {
    if (!canWrite) return;

    const next = new Set(selected);
    if (checked) next.add(permissionId);
    else next.delete(permissionId);
    setSelected(next);
    void persistPermissions(next);
  }

  if (loading) return <p className="erp-loading">Carregando...</p>;

  return (
    <PageShell>
      <div>
        <Link to="/admin/roles" className="erp-link-back">
          ← Voltar para perfis de acesso
        </Link>
        <PageHeader
          title={`Permissões — ${role?.name ?? ''}`}
          description={
            role?.code === 'admin'
              ? 'O perfil Administrador possui acesso total implicitamente a todos os módulos.'
              : saving
                ? 'Salvando alterações...'
                : 'As alterações são salvas automaticamente ao marcar ou desmarcar uma permissão.'
          }
        />
      </div>

      {role?.code !== 'admin' ? (
        <div className="space-y-4">
          {grouped.map(({ moduleCode, moduleName, pillar, permissions }) => {
            const subtitle =
              pillar && !moduleName.includes(`— ${pillar}`) ? pillar : undefined;

            return (
              <PageCard key={moduleCode} title={moduleName} subtitle={subtitle}>
                <div className="erp-form-grid md:grid-cols-3">
                  {permissions.map((permission) => (
                    <label key={permission.id} className="erp-label-inline">
                      <input
                        type="checkbox"
                        disabled={!canWrite}
                        checked={selected.has(permission.id)}
                        onChange={(event) => togglePermission(permission.id, event.target.checked)}
                      />
                      {permission.name}
                    </label>
                  ))}
                </div>
              </PageCard>
            );
          })}
        </div>
      ) : null}
    </PageShell>
  );
}
